import {
  HotelTourToggleList,
  LoopTourManager,
  LoopToursReadOnly,
} from '@/components/hotels';
import { HotelTopPicksList } from '@/components/hotels/HotelTopPicksList';
import { ToursList } from '@/components/tours';
import { TourMultiSelectDialog } from '@/components/tours/TourMultiSelectDialog';
import { type Hotel } from '@/config/objects/hotels';
import { tourCrudConfig, type Tour } from '@/config/objects/tours';
import {
  toast,
  useApiClient,
  useRequireOrganization,
  type CrudConfig,
  type CrudListHeaderAction,
} from '@startupquickstart/frontend';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const CANDIDATE_LIMIT = 100;

export function HotelToursTab() {
  const { id } = useParams<{ id: string }>();
  // Tour assignment + loop-tour curation are internal-only; hotel users get a
  // read-only / enable-disable experience instead.
  const { isInternal } = useRequireOrganization();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const { data: hotelData } = useQuery<{ success: boolean; data: Hotel }>({
    queryKey: ['Hotel', id],
    queryFn: () => apiClient.get(`/hotels/${id}`),
    enabled: !!id,
  });

  const hotel = hotelData?.data;

  // Tours that could be linked: scope to the hotel's greater area when known,
  // exclude tours already linked to this hotel, and exclude tours that are
  // assigned as some hotel's loop tour (those are managed separately).
  const candidateWhere = useMemo(() => {
    if (!hotel) return undefined;
    const base: Record<string, unknown> = {
      hotels: { none: { hotelId: hotel.id } },
      shortLoopTourHotel: { is: null },
      longLoopTourHotel: { is: null },
    };
    if (hotel.greaterAreaId) base.greaterAreaId = hotel.greaterAreaId;
    return base;
  }, [hotel]);

  const { data: candidateData, isLoading: candidatesLoading } = useQuery<{
    data: Tour[];
  }>({
    queryKey: ['HotelToursTab', 'candidates', id, candidateWhere],
    enabled: addOpen && !!id && !!candidateWhere,
    queryFn: () =>
      apiClient.post<{ data: Tour[] }>('/tours/list', {
        page: 1,
        limit: CANDIDATE_LIMIT,
        orderBy: { name: 'asc' },
        where: candidateWhere,
      }),
  });

  const { data: linkedToursData } = useQuery<{ data: Tour[] }>({
    queryKey: ['HotelTours', id],
    enabled: addOpen && !!id,
    queryFn: () => apiClient.get<{ data: Tour[] }>(`/hotels/${id}/tours?limit=200`),
  });

  const linkedTourIds = useMemo(
    () => new Set((linkedToursData?.data ?? []).map((tour) => tour.id)),
    [linkedToursData?.data],
  );

  const handleAddMany = async (selected: Tour[]) => {
    if (!id || selected.length === 0) return;
    const results = await Promise.allSettled(
      selected.map((tour) => apiClient.post(`/hotels/${id}/tours`, { tourId: tour.id })),
    );

    const failed = results.filter((r) => r.status === 'rejected').length;
    const succeeded = selected.length - failed;

    if (succeeded > 0) {
      toast.success(`Added ${succeeded} ${succeeded === 1 ? 'tour' : 'tours'} to hotel`);
    }
    if (failed > 0) {
      toast.error(`Failed to add ${failed} ${failed === 1 ? 'tour' : 'tours'}`);
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['Tour'] }),
      queryClient.invalidateQueries({ queryKey: ['HotelTours', id] }),
      queryClient.invalidateQueries({ queryKey: ['HotelToursTab', 'candidates', id] }),
    ]);

    setAddOpen(false);
  };

  const hotelToursConfig: CrudConfig<Tour> = useMemo(
    () => ({
      ...tourCrudConfig,
      entityName: 'Tour',
      entityNamePlural: 'Tours',
      endpoints: {
        ...tourCrudConfig.endpoints,
        list: `/hotels/${id}/tours`,
        delete: (tourId: string) => `/hotels/${id}/tours/${tourId}`,
      },
      listView: {
        ...tourCrudConfig.listView,
        actions: {
          ...tourCrudConfig.listView.actions,
          create: { enabled: false },
          remove: {
            enabled: true,
            onRemove: async (tour: Tour) => {
              await apiClient.delete(`/hotels/${id}/tours/${tour.id}`);
              toast.success('Tour removed from hotel');
            },
          },
        },
      },
    }),
    [id, apiClient],
  );

  const headerActions: CrudListHeaderAction[] = useMemo(
    () => [
      {
        key: 'add-tours',
        label: 'Add tours',
        icon: Plus,
        variant: 'default',
        onClick: () => setAddOpen(true),
      },
    ],
    [],
  );

  if (!id) return null;

  // Top of the tab: Top Picks (left) and Loop Tours (right) as side-by-side
  // card sections; the main tours list sits full-width beneath. Both sections
  // always render (loop tours show empty short/long placeholders) so the row
  // keeps a stable footprint.
  // Cards are direct grid children so the grid's default `items-stretch`
  // equalizes their heights; `h-full` lets each card fill the stretched cell.
  const loopSection = hotel
    ? isInternal
      ? <LoopTourManager hotel={hotel} className="h-full lg:col-span-1" />
      : <LoopToursReadOnly hotel={hotel} className="h-full lg:col-span-1" />
    : null;

  const topRow = (
    <div className={`grid grid-cols-1 gap-6 ${loopSection ? 'md:grid-cols-2 lg:grid-cols-3' : ''}`}>
      {/* Top Picks: half at md, two-thirds at lg. */}
      <HotelTopPicksList hotelId={id} className={loopSection ? 'h-full lg:col-span-2' : ''} />
      {/* Loop Tours: half at md, one-third at lg. */}
      {loopSection}
    </div>
  );

  // Hotel users: no assignment management — only enable/disable the tours we
  // assigned them, manage their own Top Picks, and view their loop tours.
  if (!isInternal) {
    return (
      <div className="space-y-6">
        {topRow}
        <HotelTourToggleList hotelId={id} />
      </div>
    );
  }

  // Internal users: full assignment + loop-tour management.
  return (
    <div className="space-y-6">
      {topRow}
      <ToursList
        config={hotelToursConfig}
        className="p-0"
        baseUrl="/tours"
        headerActions={headerActions}
      />

      <TourMultiSelectDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add tours to hotel"
        description={
          hotel?.greaterAreaId
            ? "Pick tours from this hotel's area to link. Tours already linked are hidden."
            : 'Pick tours to link to this hotel. Tours already linked are hidden.'
        }
        tours={candidateData?.data ?? []}
        isLoading={candidatesLoading}
        excludeIds={linkedTourIds}
        onSubmit={handleAddMany}
        emptyMessage="No tours available to add — every tour in this hotel's area is already linked."
      />
    </div>
  );
}
